import { useDailyEvent } from '@daily-co/daily-react';
import { EVENT_NAMES } from '@web-app/context/AnalyticsContext';
import { useBaseVideoCallAnalytics } from '@web-app/hooks/useBaseVideoCallAnalytics';
import { useParticipants } from '@web-app/hooks/useParticipants';
import { useCallback, useEffect, useRef } from 'react';

interface PatientVideoCallAnalyticsProps {
  roomId: string;
  appointmentId?: number;
  clientId?: number;
  providerId?: number;
}

/**
 * Hook for tracking patient video call analytics events.
 *
 * Events tracked:
 * - PATIENT_REQUESTED_TO_JOIN: When patient enters the waiting room
 * - PARTICIPANT_JOINED_CALL: When patient is admitted from waiting room (participantType: 'patient')
 * - PARTICIPANT_LEFT_CALL: When patient leaves the call (via base hook)
 *
 * Patient join flow:
 * 1. Patient hits "Join" button → Daily.co puts them in waiting room
 * 2. joined-meeting event fires → Track PATIENT_REQUESTED_TO_JOIN
 * 3. Provider admits patient → participantsCount goes from 1 to 2
 * 4. participantsCount > 1 detected → Track PARTICIPANT_JOINED_CALL
 *
 * @param roomId - The Daily room ID
 * @param appointmentId - Optional Healthie appointment ID
 * @param clientId - Optional Healthie client ID
 * @param providerId - Optional Healthie provider ID
 */
export const usePatientVideoCallAnalytics = ({
  roomId,
  appointmentId,
  clientId,
  providerId,
}: PatientVideoCallAnalyticsProps) => {
  // Use base hook for shared functionality (left-meeting, beforeunload)
  const { getEventProperties, trackEvent } = useBaseVideoCallAnalytics({
    roomId,
    appointmentId,
    clientId,
    providerId,
    participantType: 'patient',
  });

  const { participantsCount } = useParticipants();

  // Track if we've already fired events to prevent duplicates
  const hasTrackedRequestToJoin = useRef(false);
  const hasTrackedAdmission = useRef(false);

  // Track when patient enters waiting room
  // joined-meeting fires when patient connects to Daily but is still in waiting room
  // Daily.co docs: https://docs.daily.co/reference/daily-js/events/meeting-state#joined-meeting
  useDailyEvent(
    'joined-meeting',
    useCallback(() => {
      // Prevent duplicate tracking if the event fires multiple times
      if (hasTrackedRequestToJoin.current) return;
      hasTrackedRequestToJoin.current = true;

      // Patient enters waiting room - no participantType (different from actual join)
      trackEvent(EVENT_NAMES.PATIENT_REQUESTED_TO_JOIN, getEventProperties());
    }, [trackEvent, getEventProperties]),
  );

  // Track when patient is actually admitted from waiting room
  // This happens when they see other participants (provider admitted them)
  // When admitted, participantsCount goes from 1 (just patient) to 2+ (patient + provider)
  useEffect(() => {
    // Patient is admitted when they see more than 1 participant (themselves + provider)
    if (participantsCount > 1 && !hasTrackedAdmission.current) {
      hasTrackedAdmission.current = true;

      trackEvent(EVENT_NAMES.PARTICIPANT_JOINED_CALL, {
        ...getEventProperties(),
        participantType: 'patient',
      });
    }
  }, [participantsCount, trackEvent, getEventProperties]);
};
